import React from 'react';
import { StandardTableActions } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { ExpenseCategory } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Expense Categories',
    description: 'Manage expense categories and subcategories',
    permissionModuleKey: 'expense_categories',
    route: {
      list: '/expense-categories',
      new: '/expense-categories/new',
      detail: (id: number) => `/expense-categories/${id}`,
      edit: (id: number) => `/expense-categories/${id}/edit`,
    },
    api: {
      list: '/crm-expense-category',
      detail: (id: number) => `/crm-expense-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search expense categories...',
  },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['name'] },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: ExpenseCategory) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: ExpenseCategory[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          return (item.name || '').toLowerCase().includes(nameFilter.toLowerCase());
        }
        if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          return nameFilter.some((f) => value.includes((f as string).toLowerCase()));
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add Expense Category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/expense-categories/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: undefined,
    keyExtractor: (item: ExpenseCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new expense category.',
    noSearchResults: 'No expense categories found.',
    noFilterResults: 'No expense categories match the selected filters.',
  },
  delete: {
    title: 'Delete Expense Category',
    message: 'Are you sure you want to delete this category? Subcategories will be unlinked.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete expense category.',
    handler: async (id: number) => {
      const { deleteExpenseCategory } = await import('../../services/mutations');
      await deleteExpenseCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '-'),
  },
  detail: {
    subtitle: 'Expense Category Details',
    notFoundTitle: 'Category not found',
    notFoundMessage: "This expense category doesn't exist.",
    notFoundBackText: 'Back to Expense Categories',
    defaultTitle: 'Unnamed Category',
    getTitle: (item: ExpenseCategory) => item.name || `Category #${item.id}`,
  },
  form: {
    new: {
      title: 'New Expense Category',
      subtitle: 'Add a new expense category',
      submitText: 'Create',
    },
    edit: {
      title: 'Edit Expense Category',
      subtitle: 'Update category name',
      submitText: 'Update',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Failed to save.',
    create: async (data: Partial<ExpenseCategory>) => {
      const { createExpenseCategory } = await import('../../services/mutations');
      return await createExpenseCategory(data);
    },
    update: async (id: number, data: Partial<ExpenseCategory>) => {
      const { updateExpenseCategory } = await import('../../services/mutations');
      return await updateExpenseCategory(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter category name',
      validation: { required: true, minLength: 1, maxLength: 255 },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      { id: 'basic', title: 'Category', icon: '📝', fields: ['name'], columns: 1, collapsible: false },
    ],
  } as FormLayout,
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<ExpenseCategory>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (value) => value || '-' },
];

export const getDetailSections = (
  item: ExpenseCategory,
  _formatDate: (dateString?: string) => string,
  subcategoriesContent?: React.ReactNode
) => {
  const sections: Array<{
    title: string;
    fields?: Array<{ label: string; value: string }>;
    content?: React.ReactNode;
    condition?: boolean;
  }> = [
    {
      title: 'Basic Information',
      fields: [{ label: 'Name', value: item.name || '-' }],
    },
  ];
  if (subcategoriesContent != null) {
    sections.push({ title: 'Subcategories', content: subcategoriesContent });
  }
  return sections;
};

export const getStatusBadges = (_item: ExpenseCategory) => null;

export const getInitialFormData = (): Partial<ExpenseCategory> => ({ name: '' });

export const mapItemToFormData = (item: ExpenseCategory): Partial<ExpenseCategory> => ({
  name: item.name || '',
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getTableActions = (
  item: ExpenseCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

